import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, HeartIcon, HeartFilledIcon } from "./icons.jsx";
import { get, post } from "../lib/http.js";
import { API } from "../config/api.js";
import { absMediaUrl, formatEventScheduleLabel } from "../api/events.js";

const currencyINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n || "—";

// Put this helper near your component (once)
const formatEventNoTZ = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";

  // Read the original calendar parts in UTC to avoid day rollover
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();       // 0-11
  const day = d.getUTCDate();      // 1-31
  const hh = d.getUTCHours();      // 0-23
  const mm = d.getUTCMinutes();    // 0-59

  // Build a date for formatting (date-only) and a manual time string
  const dateOnly = new Date(Date.UTC(y, m, day));
  const timeStr =
    `${((hh % 12) || 12)}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;

  const dateStr = dateOnly.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${dateStr} • ${timeStr}`;
};


export default function EventCardNew({
  item,
  showBookButton = true,
  compact = false,
  bodyBg = "white",
}) {
  const bodyBgClass = bodyBg === "yellow" ? "bg-amber-50/50" : "bg-white";
  const navigate = useNavigate();
  const eventUrl = `/events/${item._id}`;

  const poster =
    absMediaUrl(item?.imageUrl || item?.landsacpImage?.[0]) || "";

  const [wishlisted, setWishlisted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    setImgErr(false);
  }, [poster, item?._id]);

  useEffect(() => {
    const checkWishlist = async () => {
      try {
        const raw = localStorage.getItem("bb_user");
        const parsed = raw ? JSON.parse(raw) : null;
        const userId = parsed?.id;
        const token = parsed?.token;
        if (!userId || !token) return;

        const res = await get(`${API.wishlist.getWishlist}/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const wishlistItems = res?.wishlist || res || {};
        const isWishlisted = wishlistItems?.eventIds?.some((obj) =>
          String(obj?._id ?? obj) === String(item._id)
        );
        setWishlisted(!!isWishlisted);
      } catch (err) {
        console.error("Error checking wishlist:", err);
      }
    };
    if (item?._id) checkWishlist();
  }, [item?._id]);

  const handleWishlistClick = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const raw = localStorage.getItem("bb_user");
      const parsed = raw ? JSON.parse(raw) : null;
      const userId = parsed?.id;
      const token = parsed?.token;
      if (!userId || !token) return;

      if (wishlisted) {
        await post(
          `${API.wishlist.removeFromWishlist}`,
          { userId, eventId: item._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setWishlisted(false);
      } else {
        await post(
          API.wishlist.addToWishlist,
          { userId, eventIds: [item._id] },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setWishlisted(true);
      }
    } catch (err) {
      console.error("Wishlist API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openEvent = () => {
    if (showBookButton && item?._id) navigate(eventUrl);
  };

  return (
    <div
      role={showBookButton ? "button" : undefined}
      tabIndex={showBookButton ? 0 : undefined}
      onClick={openEvent}
      onKeyDown={(e) => {
        if (showBookButton && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openEvent();
        }
      }}
      className={`group flex h-full min-w-0 max-w-full flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white font-sans shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition hover:border-amber-200/70 hover:shadow-[0_12px_32px_rgba(0,0,0,0.1)] ${showBookButton ? "cursor-pointer" : ""}`}
    >
      <div className="relative overflow-hidden bg-slate-100">
        {poster && !imgErr ? (
          <img
            src={poster}
            alt=""
            className={`${compact ? "h-40" : "h-52"} w-full object-cover transition duration-300 group-hover:scale-[1.02]`}
            loading="lazy"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div
            className={`flex w-full flex-col items-center justify-center bg-gradient-to-br from-amber-100 via-orange-50 to-slate-100 text-slate-500 ${compact ? "h-40" : "h-52"}`}
          >
            <span className="text-2xl" aria-hidden>
              ðŸŽ­
            </span>
            <span className="mt-1 px-3 text-center text-xs font-semibold">No poster</span>
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleWishlistClick();
          }}
          disabled={loading}
          className="absolute right-3 top-3 z-10 rounded-full bg-white/95 p-2 shadow-md ring-1 ring-black/5 hover:bg-white disabled:opacity-50"
        >
          {wishlisted ? (
            <HeartFilledIcon className="h-5 w-5 text-red-500" />
          ) : (
            <HeartIcon className="h-5 w-5 text-slate-600" />
          )}
        </button>
      </div>

      {/* Body */}
      <div className={`flex min-w-0 flex-1 flex-col rounded-b-2xl ${bodyBgClass} p-4 md:p-5`}>
        <h3
          className="line-clamp-2 text-base font-bold leading-snug tracking-tight text-slate-900 md:text-lg"
          title={item.name}
        >
          {item.name}
        </h3>

        {item.venue && (
          <p
            className="mt-1.5 min-w-0 break-words line-clamp-2 text-[13px] font-medium leading-snug text-slate-600"
            title={`${item.venue.address} ${item.venue.city}`}
          >
            {item.venue.address} {item.venue.city}
          </p>
        )}
        {(item.date || item.showtimes?.length) && (
          <div className="mt-2 flex items-start gap-2 text-[13px] font-medium text-slate-800">
            <CalendarIcon />
            <span className="min-w-0 line-clamp-2 leading-snug">
              {item.showtimes?.length
                ? formatEventScheduleLabel(item)
                : formatEventNoTZ(item.date)}
            </span>
          </div>
        )}

        <div className="flex-1" />

        <div className="mt-4 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
              Starting from
            </div>
            <div className="text-xl font-extrabold leading-none text-amber-700">
              {(() => {
                if (!item.ticketCategories?.length) return "—";
                const minPrice = Math.min(...item.ticketCategories.map((t) => t.price));
                return minPrice === 0 ? "Free" : currencyINR(minPrice);
              })()}
            </div>
          </div>
          {showBookButton && (
            <span className="shrink-0 rounded-xl bg-amber-400 px-4 py-2 text-xs font-bold text-slate-900 shadow-sm sm:text-sm">
              Show details
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

